import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageIcon from '@mui/icons-material/Image';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Popover,
  useMediaQuery as useMediaQueryMUI,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DateRangePicker, defaultStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // base styles
import 'react-date-range/dist/theme/default.css'; // theme css file
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';
import FilterButton from './FilterButton';

function TopFilters({ tab, onFilterChange, wrap = true, store }) {
  const [modelFilter, setModelFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState({
    startDate: undefined,
    endDate: new Date(1),
    key: 'selection',
  });
  const [sizeFilter, setSizeFilter] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingModels, setLoadingModels] = useState(false);
  const [videoDurationFilter, setVideoDurationFilter] = useState([]);
  const [audioDurationFilter, setAudioDurationFilter] = useState([]);
  const [visibilityFilter, setVisibilityFilter] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const filtersContainerRef = useRef(null);

  const { theme } = useThemeStore();
  const isSmallMobile = useMediaQueryMUI((theme) =>
    theme?.breakpoints?.down('sm')
  );

  const visibilityOptions = [
    { label: 'All', value: 'all' },
    { label: 'Public', value: 'public' },
  ];

  const sizeOptions = [
    { label: 'Square', value: 'square' },
    { label: 'Landscape', value: 'horizontal' },
    { label: 'Portrait', value: 'vertical' },
  ];

  const audioDurationOptions = [
    { label: '< 1 min', value: [0, 59] },
    { label: '1-5 min', value: [60, 300] },
    { label: '5-15 min', value: [300, 900] },
    { label: '> 15 min', value: [900, Infinity] },
  ];

  const videoDurationOptions = [
    { label: '5 seconds', value: [0, 5] },
    { label: '10 seconds', value: [6, 10] },
  ];

  useEffect(() => {
    const fetchModels = async () => {
      if (tab === 'all') return;
      setLoadingModels(true);
      try {
        const response = await store.api.get('/v2/ai/models', {
          params: { type: tab, light: true },
        });
        if (response.data.status) {
          setAvailableModels(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching models:', error);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
    setModelFilter([]);
  }, [tab, store.api]);

  useEffect(() => {
    if (wrap) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }

    const checkScroll = () => {
      if (filtersContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          filtersContainerRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    const container = filtersContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      checkScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, [
    tab,
    modelFilter,
    dateFilter,
    sizeFilter,
    videoDurationFilter,
    audioDurationFilter,
    visibilityFilter,
    wrap,
  ]);

  const handleScroll = (direction) => {
    if (filtersContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        filtersContainerRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount);
      filtersContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleOpen = (event, menu) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(menu);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenMenu('');
  };

  const handleModelFilterChange = useCallback(
    (newSelected) => {
      setModelFilter(newSelected);
      onFilterChange('model', newSelected);
    },
    [onFilterChange]
  );

  const handleDateFilterChange = useCallback(
    (ranges) => {
      // Check clear in empty selection
      if (
        ranges.selection.key &&
        ranges.selection.key === 'clear' &&
        !dateFilter.startDate &&
        dateFilter.endDate.getTime() === new Date(1).getTime()
      ) {
        return;
      }

      // Check current selection
      const isSameSelection =
        dateFilter.startDate &&
        dateFilter.endDate &&
        ranges.selection.startDate &&
        ranges.selection.endDate &&
        dateFilter.startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        dateFilter.endDate.getTime() === ranges.selection.endDate.getTime();

      if (isSameSelection) {
        setDateFilter({
          startDate: undefined,
          endDate: new Date(1),
          key: 'selection',
        });
        onFilterChange('date', null);
      } else {
        setDateFilter(ranges.selection);
        if (ranges.selection.startDate && ranges.selection.endDate) {
          onFilterChange('date', {
            startDate: ranges.selection.startDate,
            endDate: ranges.selection.endDate,
          });
        } else {
          onFilterChange('date', null);
        }
      }
    },
    [onFilterChange, dateFilter]
  );

  const handleSizeFilterChange = useCallback(
    (newSelected) => {
      setSizeFilter(newSelected);
      onFilterChange('size', newSelected);
    },
    [onFilterChange]
  );

  const handleDurationFilterChange = useCallback(
    (newSelected) => {
      if (tab === 'audio' || tab === 'all') {
        setAudioDurationFilter(newSelected);
      } else if (tab === 'video') {
        setVideoDurationFilter(newSelected);
      }

      if (newSelected.length > 0) {
        let minDuration = Math.min(...newSelected.map((opt) => opt.value[0]));
        let maxDuration = Math.max(...newSelected.map((opt) => opt.value[1]));
        if (minDuration === -Infinity) minDuration = null;
        if (maxDuration === Infinity) maxDuration = null;

        onFilterChange('duration', { min: minDuration, max: maxDuration });
      } else {
        onFilterChange('duration', null);
      }
    },
    [onFilterChange, tab]
  );

  const handleVisibilityChange = useCallback(
    (newSelected) => {
      setVisibilityFilter(newSelected);
      onFilterChange('visibility', newSelected);
    },
    [onFilterChange]
  );

  const renderMenu = (options, selected, setSelected, attribute = null) => {
    const isArray = Array.isArray(selected);

    const isChecked = (option) => {
      if (isArray)
        return selected.some(
          (item) => JSON.stringify(item) === JSON.stringify(option)
        );
      return JSON.stringify(option) === JSON.stringify(selected);
    };

    return (
      <Popover
        open={Boolean(anchorEl) && openMenu !== ''}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 180, borderRadius: 2, p: 1 } }}
      >
        <List dense>
          {options.map((option, index) => (
            <ListItem
              key={index}
              button
              onClick={() => {
                let newSelected;
                if (isArray) {
                  const currentOption = selected.find(
                    (o) => JSON.stringify(o) === JSON.stringify(option)
                  );
                  const currentIndex = selected.indexOf(currentOption);
                  newSelected = [...selected];
                  if (currentIndex === -1) {
                    newSelected.push(option);
                  } else {
                    newSelected.splice(currentIndex, 1);
                  }
                } else {
                  newSelected =
                    JSON.stringify(selected) === JSON.stringify(option)
                      ? null
                      : option;
                }
                setSelected(newSelected);
              }}
              sx={{ borderRadius: 1 }}
            >
              <Checkbox
                edge="start"
                checked={isChecked(option)}
                tabIndex={-1}
                disableRipple
                size="small"
              />
              <ListItemText primary={attribute ? option[attribute] : option} />
            </ListItem>
          ))}
        </List>
      </Popover>
    );
  };

  return (
    <div className="relative w-full">
      {!wrap && showLeftArrow && (
        <button
          onClick={() => handleScroll('left')}
          className={cn(
            'absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-full',
            {
              'bg-[#3B3B45] text-[#7F82C3] hover:opacity-80': theme === 'dark',
              'bg-[#D4D5E6] text-[#5256A6] hover:opacity-80': theme === 'light',
            }
          )}
        >
          <ChevronLeftIcon fontSize="small" />
        </button>
      )}
      <div
        ref={filtersContainerRef}
        className={cn('flex gap-1 items-center bg-transparent', {
          'flex-wrap justify-center': wrap,
          'overflow-x-hidden': !wrap,
          'px-2': !wrap && (showLeftArrow || showRightArrow),
        })}
      >
        {tab !== 'all' && (
          <FilterButton
            label="Model"
            icon={
              <ImageIcon
                sx={{
                  color: theme === 'dark' ? '#5C5A5E' : '#9091A4',
                  fontSize: 18,
                }}
              />
            }
            badgeCount={modelFilter.length}
            onClick={(e) => handleOpen(e, 'model')}
          />
        )}
        {openMenu === 'model' &&
          renderMenu(
            availableModels,
            modelFilter,
            handleModelFilterChange,
            'abbreviation'
          )}
        <FilterButton
          label="Date"
          icon={
            <CalendarMonthIcon
              sx={{
                color: theme === 'dark' ? '#5C5A5E' : '#9091A4',
                fontSize: 18,
              }}
            />
          }
          badgeCount={dateFilter.startDate && dateFilter.endDate ? 1 : 0}
          onClick={(e) => handleOpen(e, 'date')}
        />
        {openMenu === 'date' && (
          <Popover
            open={Boolean(anchorEl) && openMenu === 'date'}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: {
                '& .rdrCalendarWrapper': {
                  display: isSmallMobile ? 'none' : 'block',
                  '& .rdrDayToday .rdrDayNumber span': {
                    '&:after': {
                      background: '#464BBA',
                    },
                  },
                  '& .rdrDayToday:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span:after, & .rdrDayToday:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span:after, & .rdrDayToday:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span:after, & .rdrDayToday:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span:after':
                    {
                      background: '#fff',
                    },
                },
                '& .rdrMonthAndYearWrapper': {
                  paddingTop: 0,
                  height: 'min-content',
                },
                '& .rdrMonth': {
                  paddingBottom: 0,
                },
                '& .rdrDefinedRangesWrapper': {
                  borderRight: 0,
                  backgroundColor: theme === 'dark' ? '#252425' : '#F7F7F8',
                  '& .rdrStaticRange': {
                    backgroundColor: theme === 'dark' ? '#252425' : '#F7F7F8',
                  },
                  '& .rdrInputRanges': {
                    padding: 0,
                  },
                },
                '& .rdrDateRangeWrapper': {
                  backgroundColor: theme === 'dark' ? '#252425' : '#F7F7F8',
                },
                '& .rdrCalendarWrapper .rdrDay': {
                  color: theme === 'dark' ? '#EFEFEF' : '#211E21',
                  '& .rdrDayNumber span': {
                    color: theme === 'dark' ? '#EFEFEF' : '#211E21',
                  },
                },
                '& .rdrCalendarWrapper .rdrDay .rdrInRange ~ .rdrDayNumber span, & .rdrCalendarWrapper .rdrDay .rdrStartEdge ~ .rdrDayNumber span, & .rdrCalendarWrapper .rdrDay .rdrEndEdge ~ .rdrDayNumber span':
                  {
                    color: '#fff',
                  },
                '& .rdrCalendarWrapper .rdrDayDisabled': {
                  backgroundColor: theme === 'dark' ? '#3B3B45' : '#EDEDF2',
                  '& .rdrDayNumber span': {
                    color: theme === 'dark' ? '#787878' : '#9091A4',
                  },
                },
                '& .rdrCalendarWrapper .rdrDayPassive': {
                  '& .rdrDayNumber span': {
                    color: theme === 'dark' ? '#858585' : '#B0B0B0',
                  },
                },
                '& .rdrCalendarWrapper .rdrDay.rdrDayDisabled': {
                  '& .rdrDayNumber span': {
                    color: theme === 'dark' ? '#787878' : '#9091A4',
                  },
                  '& .rdrInRange ~ .rdrDayNumber span': {
                    color: '#fff',
                  },
                  '& .rdrStartEdge ~ .rdrDayNumber span': {
                    color: '#fff',
                  },
                  '& .rdrEndEdge ~ .rdrDayNumber span': {
                    color: '#fff',
                  },
                },
                '& .rdrPprevButton': {
                  backgroundColor: theme === 'dark' ? '#3B3B45' : '#EDEDF2',
                  '& i': {
                    borderColor:
                      theme === 'dark'
                        ? 'transparent #EFEFEF transparent transparent'
                        : 'transparent #211E21 transparent transparent',
                  },
                },
                '& .rdrNextButton': {
                  backgroundColor: theme === 'dark' ? '#3B3B45' : '#EDEDF2',
                  '& i': {
                    borderColor:
                      theme === 'dark'
                        ? 'transparent transparent transparent #EFEFEF'
                        : 'transparent transparent transparent #211E21',
                  },
                },
                '& .rdrMonthAndYearPickers': {
                  '& .rdrMonthPicker select, & .rdrYearPicker select': {
                    color: theme === 'dark' ? '#EFEFEF' : '#211E21',
                    backgroundImage:
                      theme === 'dark'
                        ? `url("data:image/svg+xml,%3Csvg width='9px' height='6px' viewBox='0 0 9 6' version='1.1' xmlns='http://www.w3.org/2000/svg'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' transform='translate(-636.000000, -171.000000)'%3E%3Cg transform='translate(172.000000, 37.000000)' fill='%23EFEFEF' fill-rule='nonzero'%3E%3Cg transform='translate(323.000000, 127.000000)'%3E%3Cpath d='M142.280245,7.23952813 C141.987305,6.92353472 141.512432,6.92361662 141.219585,7.23971106 C140.926739,7.5558055 140.926815,8.06821394 141.219755,8.38420735 L145.498801,13 L149.780245,8.38162071 C150.073185,8.0656273 150.073261,7.55321886 149.780415,7.23712442 C149.487568,6.92102998 149.012695,6.92094808 148.719755,7.23694149 L145.498801,10.7113732 L142.280245,7.23952813 Z' id='arrow'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        : `url("data:image/svg+xml,%3Csvg width='9px' height='6px' viewBox='0 0 9 6' version='1.1' xmlns='http://www.w3.org/2000/svg'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' transform='translate(-636.000000, -171.000000)'%3E%3Cg transform='translate(172.000000, 37.000000)' fill='%23211E21' fill-rule='nonzero'%3E%3Cg transform='translate(323.000000, 127.000000)'%3E%3Cpath d='M142.280245,7.23952813 C141.987305,6.92353472 141.512432,6.92361662 141.219585,7.23971106 C140.926739,7.5558055 140.926815,8.06821394 141.219755,8.38420735 L145.498801,13 L149.780245,8.38162071 C150.073185,8.0656273 150.073261,7.55321886 149.780415,7.23712442 C149.487568,6.92102998 149.012695,6.92094808 148.719755,7.23694149 L145.498801,10.7113732 L142.280245,7.23952813 Z' id='arrow'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    '& option': {
                      backgroundColor: theme === 'dark' ? '#252425' : '#F7F7F8',
                      color: theme === 'dark' ? '#EFEFEF' : '#211E21',
                    },
                  },
                },
                '& .rdrStaticRanges': {
                  '& button': {
                    borderBottom: 0,
                  },
                  '& button:hover span': {
                    backgroundColor: theme === 'dark' ? '#3B3B45' : '#EDEDF2',
                  },
                },
              },
            }}
          >
            <DateRangePicker
              className="font-figtree"
              ranges={[dateFilter]}
              onChange={handleDateFilterChange}
              showDateDisplay={false}
              maxDate={new Date()}
              inputRanges={[]}
              moveRangeOnFirstSelection={false}
              rangeColors={['#464BBA']}
              staticRanges={[
                ...defaultStaticRanges,
                {
                  label: 'Clear',
                  range: () => ({
                    startDate: undefined,
                    endDate: new Date(1),
                    key: 'clear',
                  }),
                  isSelected: () => false,
                },
              ]}
            />
          </Popover>
        )}
        {(tab === 'image' || tab === 'video' || tab === 'all') && (
          <>
            <FilterButton
              label="Aspect ratio"
              icon={
                <OpenWithIcon
                  sx={{
                    color: theme === 'dark' ? '#5C5A5E' : '#9091A4',
                    fontSize: 18,
                  }}
                />
              }
              badgeCount={sizeFilter.length}
              onClick={(e) => handleOpen(e, 'size')}
            />
            {openMenu === 'size' &&
              renderMenu(
                sizeOptions,
                sizeFilter,
                handleSizeFilterChange,
                'label'
              )}
          </>
        )}
        {(tab === 'audio' || tab === 'all') && (
          <>
            <FilterButton
              label="Duration"
              icon={
                <AccessTimeIcon
                  sx={{
                    color: theme === 'dark' ? '#5C5A5E' : '#9091A4',
                    fontSize: 18,
                  }}
                />
              }
              badgeCount={audioDurationFilter.length}
              onClick={(e) => handleOpen(e, 'duration')}
            />
            {openMenu === 'duration' &&
              renderMenu(
                audioDurationOptions,
                audioDurationFilter,
                handleDurationFilterChange,
                'label'
              )}
          </>
        )}
        {tab === 'video' && (
          <>
            <FilterButton
              label="Duration"
              icon={
                <AccessTimeIcon
                  sx={{
                    color: theme === 'dark' ? '#5C5A5E' : '#9091A4',
                    fontSize: 18,
                  }}
                />
              }
              badgeCount={videoDurationFilter.length}
              onClick={(e) => handleOpen(e, 'duration')}
            />
            {openMenu === 'duration' &&
              renderMenu(
                videoDurationOptions,
                videoDurationFilter,
                handleDurationFilterChange,
                'label'
              )}
          </>
        )}
        <FilterButton
          label={
            visibilityFilter
              ? visibilityOptions.find(
                  (opt) => opt.value === visibilityFilter.value
                )?.label
              : 'My Creations'
          }
          icon={
            <Inventory2OutlinedIcon
              sx={{
                color: theme === 'dark' ? '#5C5A5E' : '#9091A4',
                fontSize: 18,
              }}
            />
          }
          badgeCount={visibilityFilter ? 1 : 0}
          onClick={(e) => handleOpen(e, 'visibility')}
        />
        {openMenu === 'visibility' &&
          renderMenu(
            visibilityOptions,
            visibilityFilter,
            handleVisibilityChange,
            'label'
          )}
      </div>
      {!wrap && showRightArrow && (
        <button
          onClick={() => handleScroll('right')}
          className={cn(
            'absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-full',
            {
              'bg-[#3B3B45] text-[#7F82C3] hover:opacity-80': theme === 'dark',
              'bg-[#D4D5E6] text-[#5256A6] hover:opacity-80': theme === 'light',
            }
          )}
        >
          <ChevronRightIcon fontSize="small" />
        </button>
      )}
    </div>
  );
}

export default TopFilters;
